/**
 * Dashboard entrance window — module state shared by the entrance hook and
 * the sign-out cleanup (clearPerUserDeviceState).
 *
 * The entrance (card stagger, count-ups, ring fill, funnel grow) plays during
 * the FIRST dashboard visit after sign-in / cold start only. The window opens
 * when the first dashboard card stack mounts and stays open for
 * DASHBOARD_ENTRANCE_WINDOW_MS, so widgets that mount a beat later (data
 * arriving after the skeleton) still animate with the rest. Every later return
 * to the dashboard renders in final state. Sign-out re-arms it, so the next
 * sign-in plays it again (a full reload re-arms it too — module state).
 */
export const DASHBOARD_ENTRANCE_WINDOW_MS = 2500;

let entranceStartedAt = null;

/** True until the window has opened AND elapsed. */
export function isDashboardEntranceActive(now = Date.now()) {
  return entranceStartedAt === null || now - entranceStartedAt < DASHBOARD_ENTRANCE_WINDOW_MS;
}

/** Opens the window on the first call; later calls are no-ops. */
export function startDashboardEntranceWindow(now = Date.now()) {
  if (entranceStartedAt === null) entranceStartedAt = now;
}

/** Re-arms the entrance (sign-out, tests). */
export function resetDashboardEntrance() {
  entranceStartedAt = null;
  holds = 0;
  notifyHold();
}

/**
 * Entrance HOLD (setup guide, 2026-10-01). The first-run setup guide covers
 * the dashboard; the entrance must not burn its once-per-sign-in window hidden
 * underneath it. A holder (the guide) keeps the entrance pending until its OWN
 * exit has finished; the dashboard stack then plays it visibly. A hold taken
 * within HOLD_REWIND_MS of the window opening (same commit as the guide's
 * mount) rewinds the window — it was spent under the guide.
 */
export const HOLD_REWIND_MS = 400;
let holds = 0;
const holdListeners = new Set();
const notifyHold = () => holdListeners.forEach((l) => l());

export function subscribeEntranceHold(listener) {
  holdListeners.add(listener);
  return () => holdListeners.delete(listener);
}

export function isDashboardEntranceHeld() {
  return holds > 0;
}

/** True while the entrance has not started (nothing consumed it yet). */
export function isDashboardEntranceUnstarted() {
  return entranceStartedAt === null;
}

/** Takes a hold; returns an idempotent release. */
export function holdDashboardEntrance(now = Date.now()) {
  holds += 1;
  if (entranceStartedAt !== null && now - entranceStartedAt < HOLD_REWIND_MS) entranceStartedAt = null;
  notifyHold();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    notifyHold();
  };
}
